import React, { useContext, useState, useEffect } from 'react';
import { number, shape, string } from 'prop-types';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { Hydrator } from '@thd-olt-component-react/hydrator';
import {
  Card, CardTitle, CardContent, Typography, Link, CardActions
} from '@one-thd/sui-atomic-components';
import {
  StorePod,
  StorePodName,
  StorePodAddress,
  StorePodHours,
  StorePodPhoneNumber,
  StorePodLink,
  StorePodMap
} from '@thd-olt-component-react/store-pod';
import classnames from 'classnames/bind';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { AppContext } from '@thd-nucleus/app-render';
import { debounce } from '@thd-olt-functional/utils';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { HomepageContext } from './context';
import componentStylesheet from './components.module.scss';

const componentStyles = classnames.bind(componentStylesheet);

function onClickChangeStoreLink({ channel, storeId }) {
  window.scrollTo(0, 0);
  const myStoreMobileElement = document.getElementById('myStoreMobile');
  const myStoreOverlayElement = document.getElementById('myStore-overlay');

  if (channel === 'mobile' && myStoreMobileElement) {
    // TODO: Refactor to simply `document.dispatchEvent(new Event('launchLocDrawer'));`
    // The mobile event is to support legacy header
    myStoreMobileElement.dispatchEvent(new Event('touchend'));
  }
  setTimeout(() => document.dispatchEvent(new Event('launchLocDrawer')), 0);

  // Fallback if localization drawer failed to open. User redirected to store search pg.

  if (myStoreOverlayElement.classList.contains('u--hide')) {
    if (channel === 'mobile') {
      window.location.href = `/l/search/${storeId}/list/`;
    } else {
      window.location.href = `/l/search/${storeId}/full/`;
    }
  }
}

export function StoreDetails({ id, renderWidths: { minWidthLarge } }) {
  const { store, customerType, hydrationOptions } = useContext(HomepageContext);
  const { instance } = useContext(AppContext);
  const { channel } = useContext(ExperienceContext);
  const isNativeApp = instance.expContext.experienceContext.isConsumerApp;
  const isB2B = customerType.toLowerCase() === 'b2b';

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' && window.innerWidth,
  );

  const updateViewport = debounce(300, () => {
    setWindowWidth(typeof window !== 'undefined' && window.innerWidth);
  });

  useEffect(() => {
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  const isWideMap = windowWidth < 768 && windowWidth > 566;

  const mapOptions = !isWideMap
    ? { height: '262', width: '532', zoom: 10 }
    : { height: '262', width: '736', zoom: 10 };
  const { storeId } = store;

  const storeInfoCallback = () => {
    if (window?.THD_LOCALIZER_AUTO_INIT) {
      if (window.THD_LOCALIZER_AUTO_INIT.Localizer.getLocalizedStore().storeId !== storeId) {
        window.location.reload();
      }
    }
  };
  const changeStoreButton = (
    /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
    <Link onClick={() => onClickChangeStoreLink({ channel, storeId })}>
      Change Store
    </Link>
  );

  const cardTitleHeader = (
    <Typography height="tight" variant="h3" weight="bold">
      Your Store
    </Typography>
  );

  if (typeof window !== 'undefined' && window?.LIFE_CYCLE_EVENT_BUS) {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('NATIVE_APP.STORE_INFO', storeInfoCallback);
  }
  return (
    <ErrorBoundary name="store-details">
      <QueryProvider cacheKey="storeDetails">
        <Hydrator
          id={`store-details-php-hydrator-${id}`}
          className="sui-h-full"
          scrollBuffer={hydrationOptions.SCROLL_BUFFER}
          delay={hydrationOptions.HOMEPAGE_DELAY_STANDARD}
          placeholder={hydrationOptions.PLACEHOLDER}
        >
          <Card
            grow
            PaperProps={{ className: 'sui-flex sui-h-full' }}
          >
            <CardTitle
              header={cardTitleHeader}
              action={!isNativeApp && changeStoreButton}
              disableActionSpacing
              actionPosition="center"
            />
            <CardContent className="sui-flex sui-overflow-hidden sui-flex-row sui-justify-between sui-h-full">
              <StorePod storeId={storeId} className="sui-w-full sui-flex sui-gap-4 sui-flex-col">
                <div className="sui-w-full">
                  <StorePodMap
                    storeId={storeId}
                    mapOptions={mapOptions}
                    placeholderOptions={{
                      height: `${mapOptions.height}px`,
                      width: '100%',
                    }}
                  />
                </div>
                <StorePodName className="store-podname" markup="h3" />
                <div className="store-pod-content sui-flex sui-flex-row sui-gap-4">
                  <div className="sui-flex sui-flex-col sui-w-4/6 sui-gap-4">
                    <div>
                      <Typography height="normal" weight="bold">Address</Typography>
                      <StorePodAddress height="normal" />
                    </div>
                    <div>
                      {!isB2B && (
                        <StorePodPhoneNumber
                          type="store"
                          colorOfNumber="alternate"
                          height="normal"
                          boldLabel
                          displayMultiline
                        />
                      )}
                      {isB2B && (
                        <>
                          <StorePodPhoneNumber
                            type="tool"
                            displayMultiline
                            boldLabel
                            colorOfNumber="alternate"
                            showIcon
                            height="normal"
                          />
                          <StorePodPhoneNumber
                            type="pro"
                            boldLabel
                            displayMultiline
                            colorOfNumber="alternate"
                            showIcon
                            height="normal"
                          />
                        </>
                      )}
                    </div>
                  </div>
                  <div className="sui-w-2/6">
                    <Typography height="normal" weight="bold">Hours</Typography>
                    <StorePodHours boldLabel height="normal" displayMultiline />
                  </div>
                </div>
              </StorePod>
            </CardContent>
            <CardActions position="end">
              <StorePod storeId={storeId} className="sui-w-full">
                <StorePodLink type="store" visualStyle="button" variant="secondary" />
              </StorePod>
            </CardActions>
          </Card>
        </Hydrator>
      </QueryProvider>
    </ErrorBoundary>
  );
}

StoreDetails.propTypes = {
  id: string,
  renderWidths: shape({
    minWidthLarge: number,
  }),
};

StoreDetails.defaultProps = {
  id: undefined,
  renderWidths: {
    minWidthLarge: undefined,
  },
};
